import { Search } from "@mui/icons-material";
import { Box, TextField, InputAdornment, MenuItem } from "@mui/material";
import { Controller, FieldValues, Path, useFormContext } from "react-hook-form";
import LoadingButton from "@mui/lab/LoadingButton";
import { useTranslation } from "../../../hooks/useTranslation";
import React from "react";

export interface Searchable extends FieldValues {
  search: string;
}

export type PaginatedListFilter<T extends Searchable> = {
  label: string;
  formKey: keyof T;
  options: PaginatedListFilterOption[];
};

type PaginatedListFilterOption = {
  value: string;
  label: string;
};

type PaginatedListFilterRowProps<T extends Searchable> = {
  onStartDownload: () => void;
  downloadLoading: boolean;
  addButton: React.ReactElement;
  searchable?: boolean;
  searchLabel?: string;
  filters?: PaginatedListFilter<T>[];
  downloadable?: boolean;
};

export const FilterRow = <T extends Searchable>(
  props: PaginatedListFilterRowProps<T>,
) => {
  const { translateWithoutPrefix } = useTranslation();
  const {
    addButton,
    onStartDownload,
    downloadLoading,
    searchable = true,
    searchLabel,
    filters,
    downloadable,
  } = props;

  const { register, control } = useFormContext<T>();

  return (
    <Box
      sx={{
        gap: "16px",
        display: "flex",
        alignItems: "center",
        margin: "8px 0px",
        padding: "8px 16px",
        borderRadius: "4px",
        backgroundColor: (theme) => theme.palette.customBackground.box,
      }}
    >
      {searchable && (
        <Controller<T>
          control={control}
          name={"search" as Path<T>}
          render={({ field }) => (
            <TextField
              placeholder={searchLabel}
              size="small"
              {...field}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      )}
      {filters?.map((filter) => (
        <TextField
          select
          label={filter.label}
          size="small"
          sx={{ width: "250px" }}
          key={filter.label}
          defaultValue=""
          {...register(filter.formKey as Path<T>)}
        >
          <MenuItem value="" key="none">
            {filter.label}
          </MenuItem>
          {filter.options.map((option) => (
            <MenuItem value={option.value} key={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      ))}
      <Box sx={{ flex: 1 }} />
      {downloadable && (
        <LoadingButton loading={downloadLoading} onClick={onStartDownload}>
          {translateWithoutPrefix("buttons.download")}
        </LoadingButton>
      )}
      {addButton}
    </Box>
  );
};
